import React, { useState, useEffect } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import { getTypographyClass } from "../../../utils/typography";
import { newGetBookings } from "../../../actions/auth/booking";
import { EditPenIcon, CancelIcon } from "../../../components/icons";
import TotalCard from "../../../components/total-card";
import CancelBookingConfirmationModal from "./_components/CancelBookingConfirmationModal";
import CancelBookingSuccessModal from "./_components/CancelBookingSuccessModal";
import { useCancelBooking } from "./hooks/useCancelBooking";
import SkeletonLoading from "./_components/SkeletonLoading";
import GeneralInformation from "./_components/detail-sections/GeneralInformation";
import AdditionalInformation from "./_components/detail-sections/AdditionalInformation";
import PassengerInformation from "./_components/detail-sections/PassengerInformation";
import PaymentMethodInformation from "./_components/detail-sections/PaymentMethodInformation";
import BillingInformation from "./_components/detail-sections/BillingInformation";
import ContactInformation from "./_components/detail-sections/ContactInformation";
import BookingStatus from "./_components/detail-sections/BookingStatus";
import VehicleCard from "./_components/detail-sections/VehicleCard";
import HeaderSection from "./_components/detail-sections/HeaderSection";
import BookingCancellation from "./_components/detail-sections/BookingCancellation";
import ChauffeurPickUpSpot from "./_components/detail-sections/ChauffeurPickUpSpot";
import { get, map } from "lodash";

const BookingDetailSkeleton = () => {
  return (
    <SkeletonLoading />
  );
};

const BookingDetailPage = ({ match }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const dispatch = useDispatch();
  const { loading, authDetailBooking } = useSelector((state) => state.authBooking);
  const [showCancelBookingSuccessModal, setShowCancelBookingSuccessModal] = useState(false);
  const [showCancelBookingConfirmationModal, setShowCancelBookingConfirmationModal] = useState(false);

  useEffect(() => {
    dispatch(newGetBookings({
      id: match.params.id
    }, "detail"));
  }, [dispatch, match.params.id]);

  const {
    isFetchingCancelFee,
    cancellationFee,
    handleCancelSimulation,
    isCancellingBooking,
    cancelBookingResponse,
    handleCancelBooking,
  } = useCancelBooking({
    id: match.params.id,
  });

  // Typography classes dengan language handling
  const typo12 = getTypographyClass(currentLanguage, 12);
  const typo14 = getTypographyClass(currentLanguage, 14);

  if (currentLanguage === "en") {
    moment.locale("en");
  } else {
    moment.locale("ja");
  }

  const formatDate = {
    "en": "MMM. Do, YYYY (ddd) HH:mm",
    "jp": "YYYY年M月D日(dd) HH:mm"
  }

  const formatDatePayment = {
    "en": "MMM. Do, YYYY HH:mm",
    "jp": "YYYY年M月D日 HH:mm"
  }

  const isLoading = loading || !authDetailBooking || Object.keys(authDetailBooking).length === 0;

  if (isLoading) {
    return (
      <FrontLayout>
        <BookingDetailSkeleton />
      </FrontLayout>
    );
  }

  const confirmCancelBookingHandler = () => {
    handleCancelBooking();
    setShowCancelBookingConfirmationModal(false);
    setShowCancelBookingSuccessModal(true);
  }

  const isButtonsShown = () => {
    if (authDetailBooking?.bookingStatus === "CANCELLED") {
      return false;
    }

    // Get current time in JST (UTC + 9 hours)
    const currentUTC = Date.now();
    const currentJST = new Date(currentUTC + 9 * 60 * 60 * 1000);

    // Parse endTime (garageArivalTime is UTC-based ISO)
    const endTime = new Date(authDetailBooking?.garageArivalTime);

    if (currentJST > endTime) {
      return false
    }

    return true
  }

  const getEligibilityEditBooking = () => {
    if (authDetailBooking?.requestChangeBooking?.some(item => item.status === "PENDING_APPROVAL") && authDetailBooking.user.role === "individual") {
      return false;
    }

    return true
  }

  // Edit / Cancel actions. Rendered in the desktop sidebar and, because that
  // sidebar is hidden below lg, again at the end of the mobile layout.
  const renderActionButtons = () => {
    if (!isButtonsShown()) return null;

    return (
      <div className="tw:flex tw:flex-col tw:gap-3">
        {
          !getEligibilityEditBooking() ? (
            <div className="tw:!flex tw:!flex-col tw:!items-center">
              <p className="tw:!text-[13px] tw:!font-[400] tw:!mb-0 tw:leading-[22px] tw:!text-[#7C7B7B] tw:!italic tw:!text-center">
                {t("bookingDetail.button.requestPendingConfirmation")}
              </p>
              <div className="tw:flex tw:h-[45px] tw:w-full tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] tw:border tw:border-[#d9d9d9] tw:bg-[#f3f3f3] tw:px-6 tw:py-[5px] tw:cursor-not-allowed tw:opacity-50">
                <EditPenIcon
                  color="#8d8d8d"
                  size={14}
                />
                <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#8d8d8d]">
                  {t("bookingDetail.button.edit")}
                </span>
              </div>
            </div>
          ) : (
            <Link
              to={`/booking/${authDetailBooking._id}/edit`}
              className="tw:!no-underline"
            >
              <div className="tw:flex tw:h-[45px] tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] tw:border tw:border-[#7c7b7b] tw:bg-white tw:px-6 tw:py-[5px]">
                <EditPenIcon
                  color="#282828"
                  size={14}
                />
                <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828]">
                  {t("bookingDetail.button.edit")}
                </span>
              </div>
            </Link>
          )
        }
        <button
          type="button"
          onClick={() => {
            handleCancelSimulation()
            setShowCancelBookingConfirmationModal(true);
          }}
        >
          <div className="tw:flex tw:h-[45px] tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] tw:border tw:border-[#ea4335] tw:bg-white tw:px-5 tw:py-[5px]">
            <CancelIcon
              color="#EA4335"
              size={14}
            />
            <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#ea4335]">
              {t("bookingDetail.button.cancel")}
            </span>
          </div>
        </button>
      </div>
    );
  };

  const MAP = {
    "ChIJVze90XnzImARoRp3YqEpbtU": {
      name: {
        en: "Narita Airport (NRT)",
        jp: "成田空港 (NRT)",
      },
      terminal: {
        "terminal-1": t("MapPage.NaritaAirport.NaritaAirportImageUrl1"),
        "terminal-2": t("MapPage.NaritaAirport.NaritaAirportImageUrl2"),
        "terminal-3": t("MapPage.NaritaAirport.NaritaAirportImageUrl3"),
      }
    },
    "ChIJ45IxpAtkGGAR3_hG0anDMg0": {
      name: {
        en: "Haneda Airport (HND)",
        jp: "羽田空港 (HND)",
      },
      terminal: {
        "terminal-1": t("MapPage.HanedaAirport.HanedaAirportImageUrl1"),
        "terminal-2-international": t("MapPage.HanedaAirport.HanedaAirportImageUrl2"),
        "terminal-2-domestic": t("MapPage.HanedaAirport.HanedaAirportImageUrl3"),
        "terminal-3": t("MapPage.HanedaAirport.HanedaAirportImageUrl4"),
        "terminal-1-domestic": t("MapPage.HanedaAirport.HanedaAirportImageUrl1"),
        "terminal-3-international": t("MapPage.HanedaAirport.HanedaAirportImageUrl4"),
      }
    },
    "ChIJC3Cf2PuLGGAROO00ukl8JwA": {
      name: {
        en: "Tokyo Station",
        jp: "東京駅",
      },
      terminal: t("MapPage.TokyoStation.TokyoStationImageUrl")
    },
    "ChIJdYcUblqKGGARcNsG90fDRZo": {
      name: {
        en: "Shinagawa Station",
        jp: "品川駅",
      },
      terminal: t("MapPage.ShinagawaStation.ShinagawaStationImageUrl")
    }
  }

  const getLocationMap = (location) => {
    if (!location) return false;
    const mapInfo = MAP[location?.value];
    if (!mapInfo) return false;

    return mapInfo;
  }

  const pickupLocationMap = getLocationMap(authDetailBooking?.pickupLocation);

  return (
    <FrontLayout>
      <div className="tw:bg-gradient-to-b tw:from-[#f6f6f6] tw:to-white tw:pb-8 tw:!pt-10">
        <div className="tw:mx-auto tw:max-w-[1300px] tw:px-0 tw:px-6">
          {/* Header Section */}
          <HeaderSection
            authDetailBooking={authDetailBooking}
            typo14={typo14}
          />

          {/* Main Content */}
          <div className="tw:flex tw:flex-col tw:gap-3 tw:lg:flex-row">
            {/* Left Column */}
            <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-[5px] tw:lg:gap-3">
              <div className="tw:block tw:lg:hidden tw:px-5 tw:bg-white">
                {/* Booking Status */}
                <div className="tw:mt-6">
                  <BookingStatus
                    authDetailBooking={authDetailBooking}
                    onDiscardSuccess={() => dispatch(newGetBookings({ id: match.params.id }, "detail"))}
                  />
                </div>
                {/* Vehicle Card */}
                {
                  authDetailBooking?.bookingInfo?.map((v, i) => {
                    return (
                      <VehicleCard
                        key={i}
                        index={i}
                        value={v}
                      />
                    )
                  })
                }
              </div>
              {/* General Information */}
              <GeneralInformation
                authDetailBooking={authDetailBooking}
                typo12={typo12}
                formatDate={formatDate}
              />

              {/* Additional Information & Services */}
              <AdditionalInformation
                authDetailBooking={authDetailBooking}
                typo12={typo12}
              />

              {
                (authDetailBooking?.meetAssist[0].meetAssistName === "none" && pickupLocationMap) && (
                  <ChauffeurPickUpSpot
                    typo14={typo14}
                    mapInfo={pickupLocationMap}
                    terminal={authDetailBooking?.pickupTerminal}
                    isFromAirport={authDetailBooking?.isFromAirport}
                  />
                )
              }

              {/* Passenger Information */}
              <PassengerInformation
                authDetailBooking={authDetailBooking}
                typo12={typo12}
              />

              {/* Payment Method */}
              <PaymentMethodInformation
                authDetailBooking={authDetailBooking}
                typo12={typo12}
                typo14={typo14}
                formatDatePayment={formatDatePayment}
              />

              {/* Billing Information */}
              {!authDetailBooking.user.deferredPaymentEligible && (
                <BillingInformation
                  authDetailBooking={authDetailBooking}
                  typo12={typo12}
                />
              )}

              {
                (authDetailBooking?.bookingStatus === "CANCELLED" && authDetailBooking?.paymentStatus !== "CHARGE_PENDING") && (
                  <BookingCancellation
                    typo12={typo12}
                    authDetailBooking={authDetailBooking}
                  />
                )
              }

              <div className="tw:block tw:lg:hidden">
                <TotalCard
                  cart={authDetailBooking}
                  lang={currentLanguage.toUpperCase()}
                  isFoldable={true}
                  className="tw:overflow-hidden tw:rounded-[5px]"
                />
              </div>

              {/* Action Buttons (mobile) */}
              {isButtonsShown() && (
                <div className="tw:block tw:lg:hidden tw:bg-white tw:px-5 tw:py-5">
                  {renderActionButtons()}
                </div>
              )}

              {/* Contact Note */}
              <ContactInformation
                typo14={typo14}
              />
            </div>

            {/* Right Sidebar */}
            <div className="tw:lg:flex tw:w-full tw:flex-col tw:gap-3 tw:lg:w-[340px] tw:hidden">
              <BookingStatus
                authDetailBooking={authDetailBooking}
                onDiscardSuccess={() => dispatch(newGetBookings({ id: match.params.id }, "detail"))}
              />

              {/* Vehicle Card */}
              {
                authDetailBooking?.bookingInfo?.map((v, i) => {
                  return (
                    <VehicleCard
                      key={i}
                      index={i}
                      value={v}
                    />
                  )
                })
              }

              {/* Total Box */}
              <div>
                <TotalCard
                  cart={authDetailBooking}
                  lang={currentLanguage.toUpperCase()}
                  isFoldable={true}
                  className="tw:overflow-hidden tw:rounded-[5px]"
                />
              </div>

              {/* Action Buttons */}
              {renderActionButtons()}
            </div>
          </div>
        </div>
      </div>

      <CancelBookingConfirmationModal
        show={showCancelBookingConfirmationModal}
        onClose={() => setShowCancelBookingConfirmationModal(false)}
        onConfirm={() => confirmCancelBookingHandler()}
        isLoading={isFetchingCancelFee}
        refundAmount={cancellationFee?.cancellationFee?.refundAmount}
        cancellationFee={cancellationFee?.cancellationFee?.cancelFee}
        cancellationPercent={cancellationFee?.cancellationFee?.percentCancelFee}
        desc={cancellationFee?.cancellationFee?.desc}
        meetAssistCancellationFee={cancellationFee?.cancellationFee?.meetAssistCancelFee}
        meetAssistCancellationPercent={cancellationFee?.cancellationFee?.meetAssistCancelPercent}
        meetAssistCancellationDesc={cancellationFee?.cancellationFee?.meetAssistCancelDesc}
        paymentStatus={cancellationFee?.paymentStatus}
        deferredPayment={cancellationFee?.deferredPayment}
        meetAssist={authDetailBooking?.meetAssist}
        role={authDetailBooking?.user?.role}
        totalPrice={authDetailBooking?.totalPrice}
      />

      <CancelBookingSuccessModal
        show={showCancelBookingSuccessModal}
        onClose={() => {
          setShowCancelBookingSuccessModal(false)
          dispatch(newGetBookings({
            id: cancelBookingResponse?.bookingId
          }, "detail"));
        }}
        isLoading={isCancellingBooking}
        successMessage={cancelBookingResponse?.message}
        refundAmount={cancelBookingResponse?.cancellationFee?.refundAmount}
        cancellationFee={cancelBookingResponse?.cancellationFee?.cancelFee}
        cancellationPercent={cancelBookingResponse?.cancellationFee?.percentCancelFee}
        meetAssistCancellationFee={cancelBookingResponse?.cancellationFee?.meetAssistCancelFee}
        meetAssistCancellationPercent={cancelBookingResponse?.cancellationFee?.meetAssistCancelPercent}
        meetAssistCancellationDesc={cancelBookingResponse?.cancellationFee?.meetAssistCancelDesc}
        desc={cancelBookingResponse?.cancellationFee?.desc}
        paymentStatus={cancelBookingResponse?.paymentStatus}
        deferredPayment={cancelBookingResponse?.deferredPayment}
        meetAssist={authDetailBooking?.meetAssist}
        role={authDetailBooking?.user?.role}
        totalPrice={authDetailBooking?.totalPrice}
      />
    </FrontLayout>
  );
};

export default BookingDetailPage;
